import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, BadgeCheck, Languages, MapPin, MessageCircle, CalendarDays, Loader2, Star } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { http, waLink } from "@/lib/api";
import { useWaNumber } from "@/components/site/WhatsAppFab";
import { CAT_KEY, mediaUrl } from "@/components/site/PartnerCards";
import { API } from "@/lib/api";

function Gallery({ photos, name }) {
  const [active, setActive] = useState(0);
  if (!photos.length) return <div className="aspect-[16/10] rounded-3xl bg-emerald-50" />;
  return (
    <div data-testid="partner-gallery">
      <div className="aspect-[16/10] overflow-hidden rounded-3xl bg-slate-100"><img src={mediaUrl(photos[active])} alt={name} className="h-full w-full object-cover" /></div>
      {photos.length > 1 && (
        <div className="mt-3 flex gap-2 overflow-x-auto no-scrollbar">{photos.map((p, i) => (
          <button key={p} onClick={() => setActive(i)} data-testid={`partner-gallery-thumb-${i}`} className={`h-16 w-24 shrink-0 overflow-hidden rounded-xl ring-2 transition-[box-shadow] ${i === active ? "ring-[#064E3B]" : "ring-transparent"}`}>
            <img src={mediaUrl(p)} alt="" className="h-full w-full object-cover" />
          </button>
        ))}</div>
      )}
    </div>
  );
}

function Reviews({ data }) {
  const { t } = useLang();
  return (
    <section data-testid="partner-reviews" className="mt-14">
      <h2 className="flex items-center gap-2 text-2xl font-bold text-slate-900">{t("pt_reviews_title")}
        {data.rating && <span className="inline-flex items-center gap-1 text-lg"><Star className="h-5 w-5 fill-amber-400 text-amber-400" />{data.rating}<span className="text-sm font-normal text-slate-500">· {t(data.review_count === 1 ? "pt_review_one" : "pt_review_count").replace("{n}", data.review_count)}</span></span>}</h2>
      {data.reviews.length === 0 && <p data-testid="partner-no-reviews" className="mt-3 text-slate-500">{t("pt_no_reviews")}</p>}
      <div className="mt-6 grid gap-4 md:grid-cols-2">{data.reviews.map((r) => (
        <article key={r.id} data-testid={`partner-review-${r.id}`} className="rounded-3xl border border-slate-200 bg-white p-5">
          <div className="flex items-center gap-2">
            <p className="font-semibold text-slate-900">{r.user_name.split(" ")[0]}</p>
            {r.country && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600">{r.country}</span>}
            <span className="ml-auto flex">{[1, 2, 3, 4, 5].map((n) => <Star key={n} className={`h-4 w-4 ${n <= r.rating ? "fill-amber-400 text-amber-400" : "text-slate-200"}`} />)}</span>
          </div>
          <p className="mt-2 text-sm leading-relaxed text-slate-600">{r.comment}</p>
          {r.photos?.length > 0 && <div className="mt-3 flex gap-2">{r.photos.map((p) => <img key={p} src={`${API}/review-photos/${p}`} alt="" loading="lazy" className="h-16 w-16 rounded-xl object-cover" />)}</div>}
          <p className="mt-2 text-xs text-slate-400">{r.destination_name ? `${r.destination_name} · ` : ""}{r.created_at.slice(0, 10)}</p>
        </article>
      ))}</div>
    </section>
  );
}

export default function PartnerProfile() {
  const { slug } = useParams();
  const { t } = useLang();
  const num = useWaNumber();
  const [data, setData] = useState(undefined);
  useEffect(() => { http.get(`/partners/${slug}`).then((r) => setData(r.data)).catch(() => setData(null)); }, [slug]);
  if (data === undefined) return <div className="flex justify-center py-32"><Loader2 className="h-8 w-8 animate-spin text-[#064E3B]" /></div>;
  if (!data) return <p data-testid="partner-not-found" className="mx-auto max-w-7xl px-4 py-32 text-center text-slate-500">{t("pt_not_found")}</p>;
  return (
    <div data-testid="partner-profile" className="mx-auto max-w-7xl px-4 pt-10 pb-28 sm:px-6 sm:pb-10 lg:px-8">
      <Link to="/mitra" data-testid="partner-back-link" className="inline-flex items-center gap-1 text-sm font-semibold text-[#064E3B] hover:underline"><ArrowLeft className="h-4 w-4" />{t("pt_verified_title")}</Link>
      <div className="mt-6 grid gap-10 lg:grid-cols-[1.3fr_1fr]">
        <Gallery photos={data.photos} name={data.display_name} />
        <div className="space-y-5">
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold text-emerald-800"><BadgeCheck className="h-3.5 w-3.5" />{t("pt_verified_badge")}</span>
          <h1 data-testid="partner-name" className="font-display text-4xl font-bold text-slate-900 sm:text-5xl">{data.display_name}</h1>
          <p className="text-sm font-bold uppercase tracking-wider text-[#D97706]">{t(CAT_KEY[data.category])}</p>
          {data.bio && <p data-testid="partner-bio" className="whitespace-pre-wrap leading-relaxed text-slate-600">{data.bio}</p>}
          <ul className="space-y-2 text-sm text-slate-700">
            <li className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[#064E3B]" />{t(`city_${data.city}`)}</li>
            <li className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-[#064E3B]" />{t("pt_partner_since")} {data.since}</li>
          </ul>
          {data.languages.length > 0 && (
            <div data-testid="partner-languages">
              <p className="flex items-center gap-1.5 text-sm font-bold text-slate-900"><Languages className="h-4 w-4" />{t("pt_languages")}</p>
              <div className="mt-2 flex flex-wrap gap-2">{data.languages.map((l) => <span key={l} className="rounded-full border border-slate-200 bg-white px-3 py-1 text-sm">{l}</span>)}</div>
            </div>
          )}
          {num && (
            <a href={waLink(num, t("pt_book_msg").replace("{name}", data.display_name))} target="_blank" rel="noreferrer" data-testid="partner-book-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[#E11D48] px-6 py-3.5 font-bold text-white shadow-lg shadow-rose-900/20 transition-transform hover:-translate-y-0.5">
              <MessageCircle className="h-5 w-5" />{t("pt_book_partner")}
            </a>
          )}
        </div>
      </div>
      <Reviews data={data} />
    </div>
  );
}
